import { svgPathBbox } from 'svg-path-bbox';
import svgPath from 'svgpath';
import { formatSvgPathDecimals, getVectorTextFromCache } from '@/utils/file-kit';
import { type TextFragment } from '@/utils/text-kit';
import { type ITextMark } from '@/version-types';
import { getLocalTextMarks } from '../config/text-mark-mock';
import { TextMarkSvgSupportPath, type TextMarkConfig, type TextMarkStruct } from '../types';
import { loadTextMarkSVG } from './resource';

const TEXT_MARK_STORAGE_KEY = 'textMarkResource';

/**
 * 加载划重点资源
 * @param loader - 获取划重点列表（业务数据）
 * @returns
 */
export const loadTextMarkResource = async (loader: () => Promise<TextMarkStruct[]> = getLocalTextMarks) => {
    const resource = await loader();

    localStorage.setItem(TEXT_MARK_STORAGE_KEY, JSON.stringify(resource));
    await loadTextMarkSVG(resource);

    return resource;
};

export const getTextMarkConfig: () => TextMarkStruct[] = () => {
    const resource = localStorage.getItem(TEXT_MARK_STORAGE_KEY) ?? '[]';
    return JSON.parse(resource);
};

// 线性插值函数
export const linearInterpolation = (x: number, yPoint: number[]): number => {
    const points = [
        [0, 0],
        [1, yPoint[0]],
        [50, yPoint[1]],
        [100, yPoint[2]],
    ];
    // 假设points数组是按x值升序排列的
    if (x <= points[0][0]) {
        // 如果x小于等于第一个点的x值，使用第一个点进行外插（这里简化为返回第一个点的y值）
        return points[0][1];
    } else if (x >= points[points.length - 1][0]) {
        // 如果x大于等于最后一个点的x值，使用最后一个点进行外插
        return points[points.length - 1][1];
    } else {
        // 找到x所在的直线段
        for (let i = 0; i < points.length - 1; i++) {
            if (x >= points[i][0] && x <= points[i + 1][0]) {
                // 计算斜率m和截距b
                const m = (points[i + 1][1] - points[i][1]) / (points[i + 1][0] - points[i][0]);
                const b = points[i][1] - m * points[i][0]; // 使用线性方程计算y值
                return m * x + b;
            }
        }
    } // 如果找不到所在的直线段，返回 0（理论上不会发生，除非输入有误）
    return 0;
};

type SVGElementInfo = {
    element: HTMLElement;
    width: number;
    height: number;
};

export class TextMarkSVGCreator {
    private constructor() {
        throw new Error('TextMarkCreator is a static class');
    }

    // 相对于默认字体大小缩放系数
    private static fontSizeRatio = 1;

    // 相对于默认粗细的系数
    private static thicknessRatio = 1;

    // 字间距
    private static letterSpacing = 0;

    /**
     * 根据素材配置获取文本标记的位置样式
     * @param svgSize - SVG 尺寸
     * @param fragment - 文本片段
     * @param config - 素材配置
     */
    private static getMarkPositionStyle(
        svgSize: { width: number; height: number },
        fragment: TextFragment,
        config: TextMarkConfig,
    ) {
        const { width: svgWidth, height: svgHeight } = svgSize;
        const { width: textWidth, height: textHeight, left: textLeft, top: textTop } = fragment;
        const { position, offset } = config;

        const left = textLeft + offset.x * this.fontSizeRatio - (svgWidth - textWidth) / 2;

        if (position === 'top') {
            const top = textTop + offset.y * this.fontSizeRatio;
            return `left:${left}px;top:${top}px;`;
        }

        if (position === 'center') {
            const top = textTop - svgHeight / 2 + textHeight / 2 + offset.y * this.fontSizeRatio;
            return `left:${left}px;top:${top}px;`;
        }

        if (position === 'bottom') {
            const top = textTop - svgHeight + textHeight + offset.y * this.fontSizeRatio;
            return `left:${left}px;top:${top}px;`;
        }

        return '';
    }

    /**
     * 初始化 SVG 元素
     * @param svgContent - SVG 资源内容
     * @param options - SVG 元素的样式选项
     * @returns SVG 元素及其尺寸
     */
    private static initSVGRootElementInfo(svgContent: string, options: { opacity: number }): SVGElementInfo {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgContent, 'image/svg+xml');
        const svgElement = doc.documentElement;

        svgElement.setAttribute('style', `overflow:visible;opacity:${options.opacity};position:absolute;z-index:-1;`);

        const viewBox = svgElement.getAttribute('viewBox');

        if (!viewBox) {
            return { element: svgElement, width: 0, height: 0 };
        }

        const [width, height] = viewBox.split(' ').slice(-2).map(Number);

        return { element: svgElement, width, height };
    }

    /**
     * 处理 combined 类型的划重点
     */
    private static processByCombinedType(
        svgElementInfo: SVGElementInfo,
        fragment: TextFragment,
        mark: ITextMark,
        config: TextMarkConfig,
    ) {
        const { element: svgElement } = svgElementInfo;
        const { width: textWidth, height: textHeight } = fragment;
        const { padding } = config;

        // 1. 获取内容区的位置信息（上右下左）
        const positionDataset = svgElement.getAttribute('data-content-position');
        const positions = positionDataset ? positionDataset.split(',') : [];
        const [contentTop, contentRight, contentBottom, contentLeft] = positions.map((v) => +v * this.fontSizeRatio);

        // 2. 计算内容区的宽高
        const heightDataset = svgElement.getAttribute('data-content-height');
        const contentHeight = heightDataset
            ? Number(heightDataset) * this.fontSizeRatio
            : textHeight * this.fontSizeRatio;
        const contentWidth = textWidth + padding.x * 2 * this.fontSizeRatio;

        // 3. 计算总宽高
        const totalWidth = textWidth + padding.x * 2 * this.fontSizeRatio + contentLeft + contentRight;
        const totalHeight = contentHeight + contentTop + contentBottom;

        // 4. 设置 svg root 元素样式
        const style = svgElement.getAttribute('style') || '';
        svgElement.setAttribute(
            'style',
            `${style}${this.getMarkPositionStyle({ width: totalWidth, height: totalHeight }, fragment, config)}`,
        );

        // 5. 处理 shape 元素
        const shapeElements =
            TextMarkSvgSupportPath.length > 0
                ? (Array.from(svgElement.querySelectorAll(TextMarkSvgSupportPath.join(','))) as HTMLElement[])
                : ([] as HTMLElement[]);

        if (shapeElements.length === 0) return;

        const hashId = `${Math.random()}`;
        const svgUses = svgElement.querySelectorAll('use');

        shapeElements.forEach((shape) => {
            let svgId = shape.getAttribute('id');
            if (svgId?.endsWith('$hash')) {
                svgId = svgId.replace('$hash', hashId);
                shape.setAttribute('id', svgId);
            }

            const tagName = shape.tagName;

            if (tagName === 'path') {
                // 缩放路径
                let d = shape.getAttribute('d');
                if (d) {
                    d = formatSvgPathDecimals(svgPath(d).scale(this.fontSizeRatio).toString());
                    shape.setAttribute('d', d);
                }

                // 设置填充
                const fill = shape.getAttribute('fill');
                if (fill && fill !== 'none') {
                    shape.setAttribute('fill', mark.color);
                }
            }

            if (tagName === 'rect') {
                // 设置填充
                const fill = shape.getAttribute('fill');
                if (fill && fill !== 'none') {
                    shape.setAttribute('fill', mark.color);
                }
            }

            const resizeWidthDataset = shape.getAttribute('data-resize-width');
            if (resizeWidthDataset) {
                shape.setAttribute('width', `${contentWidth}`);
            }

            const resizeHeightDataset = shape.getAttribute('data-resize-height');
            if (resizeHeightDataset) {
                shape.setAttribute('height', `${contentHeight}`);
            }

            const baseXDataset = shape.getAttribute('data-base-x');
            if (baseXDataset) {
                shape.setAttribute('x', `${Number(baseXDataset) * this.fontSizeRatio}`);
            }

            const baseYDataset = shape.getAttribute('data-base-y');
            if (baseYDataset) {
                shape.setAttribute('y', `${Number(baseYDataset) * this.fontSizeRatio}`);
            }

            const baseStrokeWidthDataset = shape.getAttribute('data-base-stroke-width');
            if (baseStrokeWidthDataset) {
                shape.setAttribute('stroke-width', `${Number(baseStrokeWidthDataset) * this.fontSizeRatio}`);
            }

            svgUses.forEach((use) => {
                let useId = use.getAttribute('xlink:href') || use.getAttribute('href');
                if (useId?.endsWith('$hash')) {
                    useId = useId.replace('$hash', hashId);
                    use.setAttribute('href', useId);
                }
            });
        });

        svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
        svgElement.setAttribute('width', `${totalWidth}px`);
        svgElement.setAttribute('height', `${totalHeight}px`);
    }

    /**
     * 处理通用类型划重点
     * @param svgElementInfo - SVG 元素及其尺寸
     * @param fragment - 文本片段
     * @param mark - 划重点数据
     * @param config - 素材配置
     */
    private static processByUniversalType(
        svgElementInfo: SVGElementInfo,
        fragment: TextFragment,
        mark: ITextMark,
        config: TextMarkConfig,
    ) {
        const { element: svgElement, width: svgWidth, height: svgHeight } = svgElementInfo;
        const { width: textWidth, height: textHeight } = fragment;
        const { padding, type, sizeRange } = config;

        const shapeElements =
            TextMarkSvgSupportPath.length > 0
                ? Array.from(svgElement.querySelectorAll(TextMarkSvgSupportPath.join(',')))
                : ([] as Element[]);
        if (shapeElements.length === 0) return;

        shapeElements.forEach((shape) => {
            const tagName = shape.tagName;
            const svgStrokeWidth = +(sizeRange[1] ?? shape.getAttribute('stroke-width') ?? 0);

            if (tagName === 'line') {
                let totalWidth = textWidth;
                const totalHeight = Number((this.thicknessRatio * (svgStrokeWidth * this.fontSizeRatio)).toFixed(1));

                const x1 = (() => {
                    const attr = shape.getAttribute('x1');
                    const value = attr ? Number(attr) : 0;
                    return (value - padding.x) * this.fontSizeRatio;
                })();

                const y1 = totalHeight / 2;

                const style = svgElement.getAttribute('style') || '';

                // 虚线处理
                const strokeDasharrayAttr = shape.getAttribute('stroke-dasharray');
                if (strokeDasharrayAttr) {
                    // 视为虚实一致处理
                    const dashWidth = Number(strokeDasharrayAttr.split(' ')?.[0]) * this.fontSizeRatio;
                    const tmpStep = Math.ceil((totalWidth - x1) / dashWidth);
                    // tmpStep 为偶数时需要加 1
                    const step = tmpStep + ((tmpStep + 1) % 2);
                    totalWidth = Number((step * dashWidth + x1 * 2).toFixed(1));

                    const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: config.position !== 'center' ? totalHeight / 2 : totalHeight }, fragment, config)}`;
                    svgElement.setAttribute('style', nextStyle);
                    shape.setAttribute('stroke-dasharray', `${dashWidth}`);
                } else {
                    const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: config.position !== 'center' ? totalHeight / 2 : totalHeight }, fragment, config)}`;
                    svgElement.setAttribute('style', nextStyle);
                }

                shape.setAttribute('x1', `${x1}`);
                shape.setAttribute('y1', `${y1}`);
                shape.setAttribute('x2', `${totalWidth - x1}`);
                shape.setAttribute('y2', `${y1}`);

                const strokeAttr = shape.getAttribute('stroke');
                if (strokeAttr && strokeAttr !== 'none') {
                    shape.setAttribute('stroke', mark.color);
                }

                const fillAttr = shape.getAttribute('fill');
                if (fillAttr && fillAttr !== 'none') {
                    shape.setAttribute('fill', mark.color);
                }

                shape.setAttribute('stroke-width', `${totalHeight}`);

                svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
                svgElement.setAttribute('width', `${totalWidth}`);
                svgElement.setAttribute('height', `${totalHeight}`);
            }

            if (tagName === 'path') {
                let d = shape.getAttribute('d');

                if (type === 'repeat') {
                    const { repeatPath = '' } = config;

                    if (d) {
                        const pathBox = svgPathBbox(d);

                        const boxWidth = pathBox[2] - pathBox[0]; // 路径的 bbox 宽度

                        const repeatSpace = boxWidth - svgWidth; // 重复路径重叠需要扣除的宽度

                        const step = Math.max(
                            Math.ceil(
                                (textWidth + (repeatSpace + padding.x * 2) * this.fontSizeRatio) /
                                    ((svgWidth + repeatSpace) * this.fontSizeRatio),
                            ),
                            1,
                        );

                        let newRepeatPath = '';
                        for (let index = 0; index < step; index++) {
                            newRepeatPath += repeatPath;
                        }

                        const reg = new RegExp(repeatPath, 'g');
                        d = formatSvgPathDecimals(
                            svgPath(d.replace(reg, newRepeatPath)).scale(this.fontSizeRatio).toString(),
                        );

                        const strokeWidth = svgStrokeWidth * this.fontSizeRatio * this.thicknessRatio;

                        const totalWidth = Number(
                            (
                                step * (svgWidth + repeatSpace) * this.fontSizeRatio -
                                repeatSpace * this.fontSizeRatio
                            ).toFixed(1),
                        );
                        const totalHeight = Number((svgHeight * this.fontSizeRatio).toFixed(1));

                        const style = svgElement.getAttribute('style') || '';
                        const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: totalHeight }, fragment, config)}`;
                        svgElement.setAttribute('style', nextStyle);

                        shape.setAttribute('d', d);
                        shape.setAttribute('fill', 'none');

                        const stroke = shape.getAttribute('stroke');
                        if (stroke && stroke !== 'none') {
                            shape.setAttribute('stroke', mark.color);
                        }

                        shape.setAttribute('stroke-width', `${strokeWidth.toFixed(1)}`);

                        svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
                        svgElement.setAttribute('width', `${totalWidth}px`);
                        svgElement.setAttribute('height', `${totalHeight}px`);
                    }
                }

                if (type === 'cover') {
                    const totalWidth = textWidth + padding.x * 2 * this.fontSizeRatio;
                    const totalHeight = textHeight + padding.y * 2 * this.fontSizeRatio;

                    if (d) {
                        d = formatSvgPathDecimals(
                            svgPath(d)
                                .scale(totalWidth / svgWidth, totalHeight / svgHeight)
                                .toString(),
                        );
                        shape.setAttribute('d', d);

                        const style = svgElement.getAttribute('style') || '';
                        const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: totalHeight }, fragment, config)}`;
                        svgElement.setAttribute('style', nextStyle);

                        const strokeWidth = svgStrokeWidth * this.fontSizeRatio * this.thicknessRatio;

                        const { enableSize } = config;

                        if (enableSize) {
                            shape.setAttribute('stroke-width', `${strokeWidth.toFixed(1)}`);
                            // 虚线处理
                            const strokeDasharrayAttr = shape.getAttribute('stroke-dasharray');
                            if (strokeDasharrayAttr) {
                                const dashWidth = strokeDasharrayAttr
                                    .split(' ')
                                    ?.map((v) => Number(v) * this.fontSizeRatio)
                                    ?.join(' ');
                                shape.setAttribute('stroke-dasharray', `${dashWidth}`);
                            }
                        }

                        const stroke = shape.getAttribute('stroke');
                        if (stroke && stroke !== 'none') {
                            shape.setAttribute('stroke', mark.color);
                        }
                        const fill = shape.getAttribute('fill');
                        if (fill && fill !== 'none') {
                            shape.setAttribute('fill', mark.color);
                        }

                        svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
                        svgElement.setAttribute('width', `${totalWidth}px`);
                        svgElement.setAttribute('height', `${totalHeight}px`);
                    }
                }

                if (type === 'stretch') {
                    const totalWidth = textWidth + padding.x * 2 * this.fontSizeRatio;
                    const totalHeight = Number(((svgHeight + padding.y * 2) * this.fontSizeRatio).toFixed(1));
                    const strokeWidth = svgStrokeWidth * this.fontSizeRatio * this.thicknessRatio;

                    if (d) {
                        if (config.forceStretch) {
                            svgElement.setAttribute('preserveAspectRatio', 'none');
                            const style = svgElement.getAttribute('style') || '';
                            const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: totalHeight }, fragment, config)}`;
                            svgElement.setAttribute('style', nextStyle);

                            shape.setAttribute('stroke-width', `${strokeWidth.toFixed(1)}`);

                            const stroke = shape.getAttribute('stroke');
                            if (stroke && stroke !== 'none') {
                                shape.setAttribute('stroke', mark.color);
                            }

                            const fill = shape.getAttribute('fill');
                            if (fill && fill !== 'none') {
                                shape.setAttribute('fill', mark.color);
                            }

                            svgElement.setAttribute('width', `${totalWidth}px`);
                            svgElement.setAttribute('height', `${totalHeight}px`);
                        } else {
                            d = formatSvgPathDecimals(
                                svgPath(d)
                                    .scale(totalWidth / svgWidth, totalHeight / svgHeight)
                                    .toString(),
                            );
                            shape.setAttribute('d', d);

                            const style = svgElement.getAttribute('style') || '';
                            const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: totalHeight }, fragment, config)}`;
                            svgElement.setAttribute('style', nextStyle);

                            shape.setAttribute('stroke-width', `${strokeWidth.toFixed(1)}`);

                            const stroke = shape.getAttribute('stroke');
                            if (stroke && stroke !== 'none') {
                                shape.setAttribute('stroke', mark.color);
                            }

                            const fill = shape.getAttribute('fill');
                            if (fill && fill !== 'none') {
                                shape.setAttribute('fill', mark.color);
                            }

                            svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
                            svgElement.setAttribute('width', `${totalWidth}px`);
                            svgElement.setAttribute('height', `${totalHeight}px`);
                        }
                    }
                }

                if (type === 'use') {
                    const totalWidth = textWidth + padding.x * 2 * this.fontSizeRatio;
                    const totalHeight = Number((this.thicknessRatio * (svgHeight * this.fontSizeRatio)).toFixed(1));
                    const svgUse = svgElement.querySelector('use');

                    if (d && svgUse) {
                        d = formatSvgPathDecimals(
                            svgPath(d)
                                .scale(this.fontSizeRatio * this.thicknessRatio)
                                .toString(),
                        );
                        shape.setAttribute('d', d);

                        // 需要处理成唯一 id
                        let svgId = shape.getAttribute('id');
                        if (svgId?.endsWith('$hash')) {
                            svgId = svgId.replace('$hash', `${Math.random()}`);
                            shape.setAttribute('id', svgId);
                            svgUse.setAttribute('href', `#${svgId}`);
                        }

                        const style = svgElement.getAttribute('style') || '';
                        const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: totalHeight }, fragment, config)}`;
                        svgElement.setAttribute('style', nextStyle);

                        const fill = shape.getAttribute('fill');
                        if (fill && fill !== 'none') {
                            shape.setAttribute('fill', mark.color);
                        }

                        svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
                        svgElement.setAttribute('width', `${totalWidth}px`);
                        svgElement.setAttribute('height', `${totalHeight}px`);

                        const children = fragment.children || [];
                        if (children.length > 0) {
                            for (let i = 0; i < children.length; i++) {
                                const use = svgUse.cloneNode() as SVGUseElement;
                                const charWidth = children[i].width - this.letterSpacing; // 字宽需要减去字间距
                                const charLeft = children[i].left;
                                const x =
                                    charLeft -
                                    fragment.left +
                                    (charWidth - svgWidth * this.fontSizeRatio * this.thicknessRatio) / 2;

                                use.setAttribute('x', `${x}`);
                                svgElement.appendChild(use);
                            }
                        }

                        svgUse.remove();
                    }
                }
            }

            if (tagName === 'rect') {
                const totalWidth = textWidth + padding.x * 2 * this.fontSizeRatio;
                const totalHeight = Number(((svgHeight + padding.y * 2) * this.fontSizeRatio).toFixed(1));
                const strokeWidth = svgStrokeWidth * this.fontSizeRatio * this.thicknessRatio;

                const fill = shape.getAttribute('fill');
                if (fill && fill !== 'none') {
                    shape.setAttribute('fill', mark.color);
                }

                const stroke = shape.getAttribute('stroke');
                if (stroke && stroke !== 'none') {
                    shape.setAttribute('stroke', mark.color);
                }

                shape.setAttribute('width', `${totalWidth}px`);
                shape.setAttribute('height', `${totalHeight}px`);

                const style = svgElement.getAttribute('style') || '';
                const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: totalHeight }, fragment, config)}`;
                svgElement.setAttribute('style', nextStyle);

                svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
                svgElement.setAttribute('width', `${totalWidth}`);
                svgElement.setAttribute('height', `${totalHeight}`);
                shape.setAttribute('stroke-width', `${strokeWidth.toFixed(1)}`);

                const x = shape.getAttribute('x');
                if (x) {
                    shape.setAttribute('x', `${Number(x) * this.fontSizeRatio}`);
                }
                const y = shape.getAttribute('y');
                if (y) {
                    shape.setAttribute('y', `${Number(y) * this.fontSizeRatio}`);
                }
                const rx = shape.getAttribute('rx');
                if (rx) {
                    shape.setAttribute('rx', `${Number(rx) * this.fontSizeRatio}`);
                }
                const ry = shape.getAttribute('ry');
                if (ry) {
                    shape.setAttribute('ry', `${Number(ry) * this.fontSizeRatio}`);
                }

                // 虚线处理
                const strokeDasharrayAttr = shape.getAttribute('stroke-dasharray');
                if (strokeDasharrayAttr) {
                    const dashWidth = strokeDasharrayAttr
                        .split(' ')
                        ?.map((v) => Number(v) * this.fontSizeRatio)
                        ?.join(' ');
                    shape.setAttribute('stroke-dasharray', `${dashWidth}`);
                }
            }

            if (tagName === 'circle' || tagName === 'ellipse') {
                if (type === 'use') {
                    const totalWidth = textWidth + padding.x * 2 * this.fontSizeRatio;
                    const totalHeight = +(this.thicknessRatio * (svgHeight * this.fontSizeRatio)).toFixed(1);
                    const svgUse = svgElement.querySelector('use');
                    const r = shape.getAttribute('r');
                    const rx = shape.getAttribute('rx');
                    const ry = shape.getAttribute('ry');
                    const cx = shape.getAttribute('cx');
                    const cy = shape.getAttribute('cy');
                    const translate = shape
                        .getAttribute('style')
                        ?.match(/translate\((.*?)\)/)?.[1]
                        ?.replace(/px/g, '')
                        ?.split(',');
                    const translateX = translate?.[0] ?? 0;
                    const translateY = translate?.[1] ?? 0;

                    if (svgUse && (r || (rx && ry && cx && cy))) {
                        let svgId = shape.getAttribute('id');
                        if (svgId?.endsWith('$hash')) {
                            svgId = svgId.replace('$hash', `${Math.random()}`);
                            shape.setAttribute('id', svgId);
                            svgUse.setAttribute('href', `#${svgId}`);
                        }

                        const style = svgElement.getAttribute('style') || '';
                        const nextStyle = `${style}${this.getMarkPositionStyle({ width: totalWidth, height: totalHeight }, fragment, config)}`;
                        svgElement.setAttribute('style', nextStyle);

                        const fill = shape.getAttribute('fill');
                        if (fill && fill !== 'none') {
                            shape.setAttribute('fill', mark.color);
                        }

                        const radius = Number(r) * this.fontSizeRatio;
                        if (r) {
                            shape.setAttribute('r', `${radius}`);
                            shape.setAttribute('cx', `${radius}`);
                            shape.setAttribute('cy', `${radius}`);
                        } else if (rx && ry && cx && cy) {
                            shape.setAttribute('rx', `${+rx * this.fontSizeRatio}`);
                            shape.setAttribute('ry', `${+ry * this.fontSizeRatio}`);
                            shape.setAttribute('cx', `${+cx * this.fontSizeRatio}`);
                            shape.setAttribute('cy', `${+cy * this.fontSizeRatio}`);
                        }

                        if (translateX && translateY) {
                            const style = shape.getAttribute('style');
                            const newStyle =
                                style?.replace(
                                    /translate\((.*?)\)/,
                                    `translate(${+translateX * this.fontSizeRatio}px, ${+translateY * this.fontSizeRatio}px)`,
                                ) ?? '';

                            shape.setAttribute('style', newStyle);
                        }

                        svgElement.setAttribute('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
                        svgElement.setAttribute('width', `${totalWidth}px`);
                        svgElement.setAttribute('height', `${totalHeight}px`);

                        const children = fragment.children || [];
                        if (children.length > 0) {
                            for (let i = 0; i < children.length; i++) {
                                const use = svgUse.cloneNode() as SVGUseElement;
                                const charWidth = children[i].width - this.letterSpacing; // 字宽需要减去字间距
                                const charLeft = children[i].left;
                                const x =
                                    charLeft -
                                    fragment.left +
                                    (charWidth - svgWidth * this.fontSizeRatio * this.thicknessRatio) / 2;

                                use.setAttribute('x', `${x}`);
                                svgElement.appendChild(use);
                            }
                        }

                        svgUse.remove();
                    }
                }
            }
        });
    }

    static create(mark: ITextMark, fragment: TextFragment, fontSize: number, letterSpacing: number) {
        const { materialId } = mark;

        // FIXME: config 应该从外部传入更合适？
        const config = getTextMarkConfig().find((item) => item.materialId == materialId)?.data;
        if (!config) return '';

        this.letterSpacing = letterSpacing;

        const { defaultFontSize } = config;
        this.fontSizeRatio = fontSize / defaultFontSize;

        const { size } = mark;
        const { sizeRange } = config;
        this.thicknessRatio = sizeRange?.length === 3 ? linearInterpolation(size, sizeRange) / sizeRange[1] : 1;

        const { src } = config;
        const svgContent = getVectorTextFromCache(src);
        if (!svgContent) return '';

        const svgElementInfo = this.initSVGRootElementInfo(svgContent, { opacity: fragment.width === 0 ? 0 : 1 });

        // // 需要将高度改成纯文本高度
        // const nextFragment = {
        //     ...fragment,
        //     height: fragment.height - fontSize * (lineSpacing - 1),
        //     top: fragment.top + (fontSize * (lineSpacing - 1)) / 2,
        // };

        const { type } = config;
        if (type === 'combined') {
            this.processByCombinedType(svgElementInfo, fragment, mark, config);
        } else {
            this.processByUniversalType(svgElementInfo, fragment, mark, config);
        }

        // 将 SVG dom 元素转换为字符串并进行编码
        const svgString = new XMLSerializer().serializeToString(svgElementInfo.element);
        return svgString;
    }
}
